import makeStyles from '@mui/styles/makeStyles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { OrderEditDialog } from '../dialogs/OrderEditDialog'
import { OrderDetailDialog } from '../dialogs/OrderDetailDialog'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import TraderDiaryTableRow from './TraderDiaryTableRow'
import { newThemeObject } from '../../newTheme'
import { useTraderDiaryFilters } from '../../hooks/useTraderDiaryFilters'
import { MassOrderInputDialog } from '../dialogs/MassOrderInputDialog'
import { newTraderDiary } from '../../constants/initialStates'
import { useState } from 'react'
import { ClearConfirmationAlert } from '../massOrderInputDialog/ClearConfirmationAlert'
import { useTraderDiaryApi } from '../../hooks/useTraderDiaryApi'
import { tokens } from '../../styles/tokens'
import { TraderDiaryTableRow as TraderDiaryRowData } from '../../redux/reducers/traderDiary'
import CardWrapper from '../common/CardWrapper'
import useMobileWidth from '../../hooks/useMobileWidth'
import { InstrumentIcon } from '../common/InstrumentIcon'
import { formatNumber } from '../../utils/numbersFormatter'
import { Checkbox } from '@mui/material'
import { format } from 'date-fns'
import { ColoredIconButton } from '../common/ColoredIconButton'
import CancelOutlinedIcon from '@mui/icons-material/CancelRounded'
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightRounded'
import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import { MASS_ORDER_INPUT_DIALOG_ID } from '../../constants/dialogConstants'
import {
  TRADER_DIARY_CANCELED,
  TRADER_DIARY_PARTIAL_SETTLED,
  TRADER_DIARY_RECEIVED,
  TRADER_DIARY_SETTLED,
} from '../../constants/codeLists'
import { TraderDiary } from '../../redux/reducers/traderDiary'

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 16px',
    padding: '12px 16px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '48px',
  },
  sectionHeaderTitle: {
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  table: {
    minWidth: 650,
  },
  iconSize: {
    height: '20px',
    width: '40px',
    lineHeight: '18px',
    fontSize: '10px',
  },
  columnCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    color: tokens.color.neutral[900],
  },
  // Karty radku deniku pro mobil - nahrazuji sirokou tabulku, aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobileCard: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mobileCardHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
  },
  mobileCardName: {
    margin: 0,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    overflowWrap: 'break-word',
  },
  mobileCardSub: {
    margin: 0,
    fontSize: '0.7rem',
    color: tokens.color.neutral[500],
  },
  mobileCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
    marginTop: theme.spacing(1),
  },
  mobileCardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  mobileCardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  mobileCardValue: {
    margin: '2px 0 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    overflowWrap: 'break-word',
  },
  mobileCardState: {
    borderRadius: '1rem',
    height: '2rem',
    alignContent: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 12px',
  },
  mobileCardStateText: {
    opacity: '0.5',
    fontWeight: '500',
    fontSize: '0.7rem',
    margin: 0,
  },
  mobileCardActions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
  },
}))

type SortField = 'creationDate' | 'client' | 'direction' | 'validity' | 'price' | 'amount' | 'security' | 'state' | 'diary'

// Radi hodnoty ruznych typu (retezce, cisla, datumy, boolean) pro razeni sloupcu deniku IZ.
const compareValues = (a: any, b: any) => {
  if (a instanceof Date && b instanceof Date) {
    const at = a.getTime()
    const bt = b.getTime()
    if (isNaN(at) && isNaN(bt)) return 0
    if (isNaN(at)) return -1
    if (isNaN(bt)) return 1
    return at - bt
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
  return a > b ? 1 : a < b ? -1 : 0
}

const getSortValue = (row: TraderDiaryRowData, field: SortField) => {
  switch (field) {
    case 'client':
      return row.companyName || `${row.clientSurname}${row.clientFirstname}`
    case 'direction':
      return row.orderDirection
    case 'validity':
      return new Date(row.validity)
    case 'security':
      return row.securityName
    default:
      return row[field]
  }
}

// Pomocne funkce pro mobilni kartu radku - stejna logika jako v TraderDiaryTableRow.
const getStateColor = (state: string) => {
  if (state === TRADER_DIARY_RECEIVED) {
    return newThemeObject.palette.state.new
  } else if (state === TRADER_DIARY_PARTIAL_SETTLED) {
    return newThemeObject.palette.state.partiallyFilled
  } else if (state === TRADER_DIARY_SETTLED) {
    return newThemeObject.palette.state.filled
  } else if (state === TRADER_DIARY_CANCELED) {
    return newThemeObject.palette.state.canceled
  }
}

const getTraderDiariesFromRow = (row: TraderDiaryRowData) => {
  // kopie, ne reference - jinak se prepisuje sdilena konstanta newTraderDiary
  // a novy zapis do deniku se predvyplni cenou posledniho realizovaneho radku
  let tmp: TraderDiary = { ...newTraderDiary }

  tmp.traderDiaryId = row.traderDiaryId
  tmp.businessTerritoryId = row.businessTerritoryId
  tmp.creationDate = row.creationDate
  tmp.amount = row.amount
  tmp.quantity = row.quantity
  tmp.price = row.price
  tmp.state = row.state

  tmp.orderDirection = row.orderDirection
  tmp.validityType = row.validityType
  tmp.validity = new Date(row.validity)
  tmp.orderType = row.orderType
  tmp.orderMarketType = row.orderMarketType

  tmp.currency = row.currency
  tmp.securityId = row.securityId

  tmp.portfolio = row.portfolio
  tmp.clientId = row.clientId
  tmp.methodReceiving = row.methodReceiving

  return tmp
}

interface TraderDiaryTableProps {
  setTraderDiaryCheck: (traderDiaryId: number, valueDiary: boolean) => any
}

export const TraderDiaryTable = ({setTraderDiaryCheck}: TraderDiaryTableProps) => {
  const classes = useStyles()
  const traderDiaryTableRows = useTraderDiaryFilters()
  const { t } = useTranslation()
  const [traderDiary, setTraderDiary] = useState(newTraderDiary)
  const [orderAlertOpen, setOrderAlertOpen] = useState(false)
  const { updateStateDiaryToCancel } = useTraderDiaryApi();
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const isMobile = useMobileWidth()
  const dispatch = useDispatch()

  const orderDirectionValue = (orderDirection: string) => {
    if (orderDirection === 'OD_BUY') {
      return t('OD_BUY')
    } else {
      return t('OD_SELL')
    }
  }

  const orderValidityValue = (orderValidity: string | number) => {
    if (orderValidity === 'OV_GTC') {
      return t('OV_GTC')
    } else {
      return t('OV_GTD')
    }
  }

  const orderValidityType = (validityType: string, validityDate: string) => {
    if (validityType === 'OV_GTD') {
      return validityDate ? format(new Date(validityDate), 'dd/MM/yyyy') : ''
    }
    return ''
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedTableRows = [...traderDiaryTableRows]
  if (sortField) {
    sortedTableRows.sort((a, b) => {
      const result = compareValues(getSortValue(a, sortField), getSortValue(b, sortField))
      return sortDirection === 'asc' ? result : -result
    })
  }

  const orderAlertOk = () => {
    setOrderAlertOpen(false)
    cancleTraderDiary()
  }

  const orderAlertCancel = () => {
    setOrderAlertOpen(false)
  }

  const cancleTraderDiary = () => {
    updateStateDiaryToCancel(traderDiary.traderDiaryId)
  }
  
  return (
    <>
      <TableContainer component={CardWrapper}>
        <div className={classes.sectionHeader}>
          <p className={classes.sectionHeaderTitle}>{t('EVIDENCE_RECEIVED_ORDERS')}</p>
        </div>
        {isMobile ? (
          <div className={classes.mobileList}>
            {sortedTableRows.map((row, index) => (
              <div className={classes.mobileCard} key={index}>
                <div className={classes.mobileCardHeader}>
                  <InstrumentIcon securityType={row.assetClass} />
                  <div className={classes.mobileCardHeaderText}>
                    {row.companyName ? (
                      <p className={classes.mobileCardName}>{row.companyName}</p>
                    ) : (
                      <p className={classes.mobileCardName}>
                        {row.clientSurname} {row.clientFirstname}
                      </p>
                    )}
                    <p className={classes.mobileCardSub}>{row.securityName}</p>
                  </div>
                  <p className={classes.mobileCardValue}>
                    {formatNumber(row.amount)} {row.currency}
                  </p>
                </div>
                <div className={classes.mobileCardGrid}>
                  <div className={classes.mobileCardField}>
                    <p className={classes.mobileCardLabel}>{t('CRETATION_DATE')}</p>
                    <p className={classes.mobileCardValue}>{format(new Date(row.creationDate), 'dd/MM/yyyy')}</p>
                  </div>
                  <div className={classes.mobileCardField}>
                    <p className={classes.mobileCardLabel}>{t('DIRECTION_ORDER_TYPE')}</p>
                    <p className={classes.mobileCardValue}>{orderDirectionValue(row.orderDirection)}</p>
                  </div>
                  <div className={classes.mobileCardField}>
                    <p className={classes.mobileCardLabel}>{t('DATE_FROM')}</p>
                    <p className={classes.mobileCardValue}>
                      {orderValidityValue(row.validityType)} {orderValidityType(row.validityType, row.validity)}
                    </p>
                  </div>
                  <div className={classes.mobileCardField}>
                    <p className={classes.mobileCardLabel}>{t('PRICE_UNIT')}</p>
                    <p className={classes.mobileCardValue}>
                      {formatNumber(row.price)}{' '}
                      {formatNumber(row.quantity, { decimalPlaces: 0, suffix: `${t('UNIT')}` })}
                    </p>
                  </div>
                  <div className={classes.mobileCardField}>
                    <p className={classes.mobileCardLabel}>{t('INSTRUMENT')}</p>
                    <p className={classes.mobileCardValue}>
                      {row.isin}
                      {row.symbol ? `/${row.symbol}` : ''}
                    </p>
                  </div>
                  <div className={classes.mobileCardField}>
                    <p className={classes.mobileCardLabel}>{t('ORDER_STATE')}</p>
                    <div style={{ backgroundColor: getStateColor(row.state) }} className={classes.mobileCardState}>
                      <p className={classes.mobileCardStateText}>{t(row.state)}</p>
                    </div>
                  </div>
                </div>
                <div className={classes.mobileCardActions}>
                  <Checkbox
                    disabled={row.diaryExported}
                    checked={row.diary}
                    onChange={() => setTraderDiaryCheck(row.traderDiaryId, row.diary)}
                    name="checked"
                    color="primary"
                  />
                  <ColoredIconButton
                    size="small"
                    onClick={() => {
                      setTraderDiary(getTraderDiariesFromRow(row))
                      dispatch(
                        openDialog(MASS_ORDER_INPUT_DIALOG_ID, {
                          clientId: row.clientId,
                          securityId: row.securityId,
                          orderId: row.traderDiaryId,
                        })
                      )
                    }}
                    icon={<ArrowCircleRightOutlinedIcon fontSize="small" />}
                    title={t('REALIZE_ORDER')}
                    disabled={row.state !== TRADER_DIARY_RECEIVED}
                  />
                  <ColoredIconButton
                    size="small"
                    onClick={() => {
                      setTraderDiary(getTraderDiariesFromRow(row))
                      setOrderAlertOpen(true)
                    }}
                    icon={<CancelOutlinedIcon fontSize="small" />}
                    title={t('TRADER_CANCEL')}
                    disabled={row.state !== TRADER_DIARY_RECEIVED}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
        <Table className={`${classes.table}`} size="small" aria-label="simple table">
          <TableHead>
            <TableRow className={classes.tableHead}>
              <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])} />
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'creationDate'}
                  direction={sortField === 'creationDate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('creationDate')}
                >
                  {t('CRETATION_DATE')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'client'}
                  direction={sortField === 'client' ? sortDirection : 'asc'}
                  onClick={() => handleSort('client')}
                >
                  {t('CLIENT')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'direction'}
                  direction={sortField === 'direction' ? sortDirection : 'asc'}
                  onClick={() => handleSort('direction')}
                >
                  {t('DIRECTION_ORDER_TYPE')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'validity'}
                  direction={sortField === 'validity' ? sortDirection : 'asc'}
                  onClick={() => handleSort('validity')}
                >
                  {t('DATE_FROM')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'price'}
                  direction={sortField === 'price' ? sortDirection : 'asc'}
                  onClick={() => handleSort('price')}
                >
                  {t('PRICE_UNIT')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'amount'}
                  direction={sortField === 'amount' ? sortDirection : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  {t('AMOUNT_CURRENCY')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'security'}
                  direction={sortField === 'security' ? sortDirection : 'asc'}
                  onClick={() => handleSort('security')}
                >
                  {t('INSTRUMENT')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'state'}
                  direction={sortField === 'state' ? sortDirection : 'asc'}
                  onClick={() => handleSort('state')}
                >
                  {t('ORDER_STATE')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'diary'}
                  direction={sortField === 'diary' ? sortDirection : 'asc'}
                  onClick={() => handleSort('diary')}
                >
                  {t('INVESTOR_DIARY')}
                </TableSortLabel>
              </TableCell>
              <TableCell  className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])}/>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTableRows.map((row, index) => (
              <TraderDiaryTableRow
                key={index} row={row}
                isLast={index === sortedTableRows.length - 1}
                setTraderDiaryCheck={setTraderDiaryCheck}
                setTraderDiary={setTraderDiary}
                setOrderAlertOpen={setOrderAlertOpen}
              />
            ))}
          </TableBody>
        </Table>
        )}
      </TableContainer>
      <OrderEditDialog />
      <OrderDetailDialog />
      <MassOrderInputDialog traderDiary={traderDiary} isFromTrader={true}/>
      <ClearConfirmationAlert
          alertOpen={orderAlertOpen}
          onOk={orderAlertOk}
          onCancel={orderAlertCancel}
          alertText={t('TRADER_CANCEL_MESSAGE')}
          inProgress={false}
      />
      
    </>
  )
}
